import { ComponentType } from '@angular/cdk/overlay';
import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input, signal } from '@angular/core';

import {
  GuaranteeApplication,
  GuaranteeApplicationStatus,
  GuaranteeApplicationsService,
  GuaranteeStatus,
  Prescription,
  PrescriptionStatus,
  PrescriptionTypes,
  isVisiblePrescription,
} from '@lib/prescriptions/data-access';
import { PrescriptionItemComponent } from '@lib/prescriptions/feature';
import {
  FreeFormModalComponent,
  MedicationPrescriptionFormModalComponent,
  PersonalDoctorPrescriptionFormModalComponent,
  ServicePrescriptionFormModalComponent,
} from '@lib/prescriptions/ui';
import { TranslatePipe } from '@ngx-translate/core';
import { map, take } from 'rxjs';

import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';

import { SpinLoadingComponent } from '@lib/shared-ui/commons/spin-loading/spin-loading.component';

@Component({
  selector: 'members-appointment-guarantee',
  imports: [NgOptimizedImage, MatIcon, MatMenu, MatMenuItem, MatMenuTrigger, TranslatePipe, PrescriptionItemComponent, SpinLoadingComponent],
  templateUrl: './appointment-guarantee.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppointmentGuaranteeComponent implements OnInit {
  private readonly guaranteesService = inject(GuaranteeApplicationsService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);

  readonly memberId = input.required<string>();
  readonly appointmentId = input.required<string>();

  protected readonly prescriptionTypes = PrescriptionTypes;

  protected readonly guarantee = signal<GuaranteeApplication | null>(null);
  protected readonly loading = signal<boolean>(false);
  protected readonly similarCheckTrigger = signal<number>(0);

  protected readonly prescriptions = computed<Prescription[]>(() => (this.guarantee()?.prescriptions ?? []).filter(isVisiblePrescription));
  protected readonly canProcess = computed<boolean>(() => this.guarantee()?.status === GuaranteeApplicationStatus.WaitProcessing);
  protected readonly hasUnprocessedPrescriptions = computed<boolean>(() =>
    this.prescriptions().some(prescription => prescription.guaranteeStatus === GuaranteeStatus.Unknown || prescription.status === PrescriptionStatus.WaitProcessing)
  );

  ngOnInit(): void {
    this.loadGuarantee();
  }

  protected refresh(updated: boolean): void {
    if (updated) this.loadGuarantee();
  }

  protected checkAllSimilar(): void {
    this.similarCheckTrigger.update(value => value + 1);
  }

  protected processGuarantee(): void {
    this.loading.set(true);
    this.guaranteesService
      .processGuaranteeApplication(this.memberId(), this.guarantee().id)
      .pipe(take(1))
      .subscribe({
        next: () => this.loadGuarantee(),
        error: () => {
          this.snackBar.open('Не вдалось опрацювати зміни призначеннях', 'ОК');
          this.loading.set(false);
        },
      });
  }

  protected addPrescription(prescriptionType: PrescriptionTypes): void {
    let componentToOpen: ComponentType<unknown>;

    switch (prescriptionType) {
      case PrescriptionTypes.Drugs:
        componentToOpen = MedicationPrescriptionFormModalComponent;
        break;
      case PrescriptionTypes.PersonalDoctorAppointment:
        componentToOpen = PersonalDoctorPrescriptionFormModalComponent;
        break;
      default:
        componentToOpen = ServicePrescriptionFormModalComponent;
        break;
    }

    const dialogRef = this.dialog.open(componentToOpen, {
      maxHeight: '90vh',
      width: '80vw',
      maxWidth: '768px',
      data: {
        prescription: null,
        prescriptionType,
        memberId: this.memberId(),
        guaranteeApplicationId: this.guarantee().id,
      },
    });

    this.handleAddPrescriptionClose(dialogRef);
  }

  protected addFreeFormPrescription(): void {
    const dialogRef = this.dialog.open(FreeFormModalComponent, {
      maxHeight: '90vh',
      width: '80vw',
      maxWidth: '768px',
      disableClose: true,
      data: {
        prescription: null,
        memberId: this.memberId(),
        guaranteeApplicationId: this.guarantee().id,
        mode: 'managed',
        isFreeForm: true,
      },
    });

    this.handleAddPrescriptionClose(dialogRef);
  }

  private handleAddPrescriptionClose(dialogRef: MatDialogRef<unknown>): void {
    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (result) this.loadGuarantee();
      });
  }

  private loadGuarantee(): void {
    this.loading.set(true);
    this.guaranteesService
      .searchGuaranteeApplications({ page: 1, pageSize: 1, appointmentId: this.appointmentId() })
      .pipe(
        map(response => response.value[0] ?? null),
        take(1)
      )
      .subscribe({
        next: guarantee => {
          this.guarantee.set(guarantee);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }
}
